// PO VASP parser — maps the admin-service VASP wire shape (camelCase) to the
// module view-model types. List rows and detail share the core registration
// fields; the detail endpoint additionally carries the Ctrl-Alt v2 extras
// (participant number, Emirates-ID identifier, DLD verification), which never
// appear on a list row.

import type { VaspApproveResult } from '@shared/constants/vaspSubmit'
import type {
  PropertyOwnerVaspDetails,
  PropertyOwnerVaspRow,
  PropertyOwnerVaspVerification,
  VaspUserStatus,
} from '../types/propertyOwnerTypes'

// Loose wire shape — every field optional/unknown before parsing.
export interface PropertyOwnerVaspDetailsDto {
  id?: string
  propertyOwnerId?: string
  ownerName?: string | null
  ownerEmail?: string | null
  vaspProvider?: string
  vaspUserId?: string
  vaspUserStatus?: string
  vaspRejectionReason?: string | null
  vaspRegisteredAt?: string | null
  lastAttemptAt?: string | null
  createdAt?: string
  participantNumber?: string | null
  thirdPartyIdentifier?: string | null
  thirdPartyIdentifierType?: string | null
  /**
   * Every verification Ctrl-Alt has run against the organisation, newest first —
   * the audit trail behind a `dld_verification` submit. `verification` is the
   * pre-array shape; drop it once admin-service emits the array everywhere.
   */
  verifications?: PropertyOwnerVaspVerificationDto[] | null
  verification?: {
    type?: string
    status?: string
    createdAt?: string
    lastCompletedAt?: string | null
    error?: string | null
  } | null
  // Outcome of the last admin-initiated Ctrl-Alt call (approve / reject),
  // persisted on the vasp_users row so the reason outlives the request. Absent
  // on rows that predate the columns, and cleared by a successful attempt.
  // `lastError` arrives source-prefixed (e.g. "Ctrl-Alt: <sentence>").
  lastError?: string | null
  lastErrorSource?: string | null
  lastRequestId?: string | null
  retryCount?: number | null
  lastSyncedAt?: string | null
}

export type PropertyOwnerVaspVerificationDto = NonNullable<
  PropertyOwnerVaspDetailsDto['verification']
>

// Body of the approve route, which submits the Ctrl-Alt organisation for its
// lifecycle step. Mirrors the gateway's SubmitResponseDto; every field optional
// because the same shape also arrives on the error path.
export interface PropertyOwnerVaspSubmitResponseDto {
  id?: string | null
  /**
   * The status the registration landed on, read back from the provider by
   * admin-service. The only field that says whether the registration is live:
   * a passing step is a compliance check, not an activation.
   */
  vaspUserStatus?: string | null
  steps?: Array<{
    step?: string | null
    result?: { status?: string | null; failureReason?: string | null } | null
  }> | null
}

const parseVerification = (
  dto: PropertyOwnerVaspVerificationDto,
): PropertyOwnerVaspVerification => ({
  type: String(dto.type ?? ''),
  status: String(dto.status ?? ''),
  createdAt: String(dto.createdAt ?? ''),
  lastCompletedAt: dto.lastCompletedAt ?? null,
  error: dto.error ?? null,
})

// admin-service is migrating this block from one record to the full trail.
// Read the array when it arrives and lift the singular into a one-item list
// otherwise, so the drawer only ever handles one shape. Delete the singular
// branch once every VASP detail response carries `verifications`.
const parseVerifications = (dto: PropertyOwnerVaspDetailsDto): PropertyOwnerVaspVerification[] => {
  if (Array.isArray(dto.verifications)) return dto.verifications.map(parseVerification)
  return dto.verification ? [parseVerification(dto.verification)] : []
}

const parseRowFields = (dto: PropertyOwnerVaspDetailsDto): PropertyOwnerVaspRow => ({
  id: String(dto.id ?? ''),
  propertyOwnerId: String(dto.propertyOwnerId ?? ''),
  ownerName: String(dto.ownerName ?? ''),
  ownerEmail: String(dto.ownerEmail ?? ''),
  vaspProvider: String(dto.vaspProvider ?? ''),
  vaspUserId: String(dto.vaspUserId ?? ''),
  vaspUserStatus: String(dto.vaspUserStatus ?? '') as VaspUserStatus,
  vaspRejectionReason: dto.vaspRejectionReason ?? null,
  vaspRegisteredAt: dto.vaspRegisteredAt ?? null,
  lastAttemptAt: dto.lastAttemptAt ?? null,
  createdAt: String(dto.createdAt ?? ''),
})

export const parsePropertyOwnerVaspRow = (dto: PropertyOwnerVaspDetailsDto): PropertyOwnerVaspRow =>
  parseRowFields(dto)

export const parsePropertyOwnerVaspDetails = (
  dto: PropertyOwnerVaspDetailsDto,
): PropertyOwnerVaspDetails => ({
  ...parseRowFields(dto),
  participantNumber: dto.participantNumber ?? null,
  thirdPartyIdentifier: dto.thirdPartyIdentifier ?? null,
  thirdPartyIdentifierType: dto.thirdPartyIdentifierType ?? null,
  verifications: parseVerifications(dto),
  lastError: dto.lastError ?? null,
  lastErrorSource: dto.lastErrorSource ?? null,
  lastRequestId: dto.lastRequestId ?? null,
  retryCount: dto.retryCount ?? null,
  lastSyncedAt: dto.lastSyncedAt ?? null,
})

// The submit body, returned identically on a pass and — via the 409 error
// envelope — on a failed step. Kept lossless: `submitStepViews` in
// @shared/constants/vaspSubmit owns every label and fallback, so a step this
// parser has never seen still renders.
export const parsePropertyOwnerVaspSubmitResponse = (
  dto: PropertyOwnerVaspSubmitResponseDto,
): VaspApproveResult => ({
  id: String(dto.id ?? ''),
  vaspUserStatus: String(dto.vaspUserStatus ?? ''),
  steps: Array.isArray(dto.steps)
    ? dto.steps.map((entry) => ({
        step: entry.step ?? null,
        result: entry.result
          ? {
              status: entry.result.status ?? null,
              failureReason: entry.result.failureReason ?? null,
            }
          : null,
      }))
    : [],
})
